import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { flagInboundMissingReturn } from "./schema/flag-inbound-missing-return";
import { get3plDailyItemsProcessed } from "./schema/get-3pl-daily-items-processed";
import { get3plDashboardMetrics } from "./schema/get-3pl-dashboard-metrics";
import { get3plLeaderboard } from "./schema/get-3pl-leaderboard";
import { getAiExchangeAgentAnalytics } from "./schema/get-ai-exchange-agent-analytics";
import { getClaimAttachmentRateSeriesData } from "./schema/get-claim-attachment-rate-series-data";
import { getClaimsAnalyticsStatistics } from "./schema/get-claims-analytics-statistics";
import { getInboundDashboard } from "./schema/get-inbound-dashboard";
import { getInboundDashboardReturns } from "./schema/get-inbound-dashboard-returns";
import { getInboundDashboardSortingVolume } from "./schema/get-inbound-dashboard-sorting-volume";
import { getOperationsNewTotals } from "./schema/get-operations-new-totals";
import { getOrderSourceNames } from "./schema/get-order-source-names";
import { getPortalEvents } from "./schema/get-portal-events";
import { getPortalOptimizationState } from "./schema/get-portal-optimization-state";
import { getProcessingPipeline } from "./schema/get-processing-pipeline";
import { getProcessingPipelineReturns } from "./schema/get-processing-pipeline-returns";
import { getProductTitles } from "./schema/get-product-titles";
import { getProductTypes } from "./schema/get-product-types";
import { getQaMetricsDashboard } from "./schema/get-qa-metrics-dashboard";
import { getRefulfillmentDashboard } from "./schema/get-refulfillment-dashboard";
import { getRefulfillmentDashboardHistory } from "./schema/get-refulfillment-dashboard-history";
import { getRefulfillmentDashboardOrders } from "./schema/get-refulfillment-dashboard-orders";
import { getReturnAnalyticsByProduct } from "./schema/get-return-analytics-by-product";
import { getReturnAnalyticsByVariant } from "./schema/get-return-analytics-by-variant";
import { getReturnAnalyticsSeriesData } from "./schema/get-return-analytics-series-data";
import { getReturnAnalyticsStatistics } from "./schema/get-return-analytics-statistics";
import { getReturnOperationsKeyStats } from "./schema/get-return-operations-key-stats";
import { getTags } from "./schema/get-tags";
import { getTryFirstAnalytics } from "./schema/get-try-first-analytics";
import { getVendorNames } from "./schema/get-vendor-names";
import {
  createReport,
  deleteCustomAnalyticsReport,
  deleteReport,
  duplicateCustomAnalyticsReport,
  duplicateCustomAnalyticsReportDraft,
  getCustomAnalyticsReport,
  getCustomAnalyticsReportColumnCatalog,
  getCustomAnalyticsReportDraftRows,
  getCustomAnalyticsReportFeedback,
  getCustomAnalyticsReportFilterOptions,
  getCustomAnalyticsReportRows,
  getCustomAnalyticsReportStructure,
  getReport,
  getReports,
  listCustomAnalyticsReportRevisions,
  listCustomAnalyticsReports,
  previewCustomAnalyticsReportColumns,
  regenerateCustomAnalyticsReport,
  restoreCustomAnalyticsReportRevision,
  saveCustomAnalyticsReportDraft,
  streamCustomAnalyticsReportData,
  streamCustomAnalyticsReportDraftData,
  streamOrderedLineItemsReportData,
  streamPurchasedExtendedWarrantyReportData,
  streamReturnedLineItemsReportData,
  streamReturnsReportData,
  streamWarrantyCustomersReportData,
  submitCustomAnalyticsReportFeedback,
  updateCustomAnalyticsReport,
  updateCustomAnalyticsReportSchedule,
  updateCustomAnalyticsReportTitle,
  updateReport,
} from "./schema/reports";

export const analyticsMerchantRpcDefinition = {
  createReport,
  deleteReport,
  getAiExchangeAgentAnalytics,
  getPortalEvents,
  getPortalOptimizationState,
  getClaimAttachmentRateSeriesData,
  getClaimsAnalyticsStatistics,
  getOperationsNewTotals,
  getProductTitles,
  getCustomAnalyticsReport,
  getCustomAnalyticsReportStructure,
  getCustomAnalyticsReportColumnCatalog,
  previewCustomAnalyticsReportColumns,
  getCustomAnalyticsReportFilterOptions,
  getCustomAnalyticsReportRows,
  getCustomAnalyticsReportDraftRows,
  getCustomAnalyticsReportFeedback,
  submitCustomAnalyticsReportFeedback,
  deleteCustomAnalyticsReport,
  duplicateCustomAnalyticsReport,
  duplicateCustomAnalyticsReportDraft,
  listCustomAnalyticsReportRevisions,
  regenerateCustomAnalyticsReport,
  restoreCustomAnalyticsReportRevision,
  updateCustomAnalyticsReport,
  saveCustomAnalyticsReportDraft,
  updateCustomAnalyticsReportSchedule,
  updateCustomAnalyticsReportTitle,
  getReport,
  getReports,
  listCustomAnalyticsReports,
  streamCustomAnalyticsReportData,
  streamCustomAnalyticsReportDraftData,
  get3plDailyItemsProcessed,
  get3plDashboardMetrics,
  get3plLeaderboard,
  getInboundDashboard,
  getInboundDashboardReturns,
  getInboundDashboardSortingVolume,
  getProcessingPipeline,
  getProcessingPipelineReturns,
  getQaMetricsDashboard,
  getRefulfillmentDashboard,
  getRefulfillmentDashboardHistory,
  getRefulfillmentDashboardOrders,
  getReturnAnalyticsByProduct,
  getReturnAnalyticsByVariant,
  getReturnAnalyticsSeriesData,
  getReturnAnalyticsStatistics,
  getReturnOperationsKeyStats,
  getTryFirstAnalytics,
  streamReturnedLineItemsReportData,
  streamReturnsReportData,
  streamOrderedLineItemsReportData,
  streamWarrantyCustomersReportData,
  streamPurchasedExtendedWarrantyReportData,
  getTags,
  getProductTypes,
  getVendorNames,
  getOrderSourceNames,
  updateReport,
  flagInboundMissingReturn,
} as const;

export type AnalyticsMerchantRpcDefinition = InferRpcDefinition<
  typeof analyticsMerchantRpcDefinition
>;
